(ns minorodata.app.featurebase
  (:require ["featurebase-js" :default Featurebase
             :refer [boot initChangelog destroyChangelog clearConfig
                     getUnviewedChangelogCount openChangelogPopup
                     resolveOrganization setTheme shutdown update]
             :rename {update update-user}]
            [applied-science.js-interop :as j]
            [clojure.string :as str]
            [minorodata.app.api-config :refer [AUTH_API_HOST FEATUREBASE_APP_ID]]
            [minorodata.app.handlers :refer [dispatch reg-fx]]
            [minorodata.app.utils :refer [try-parse-features]]
            [mold-http.core :refer [reg-event-http]]
            [re-frame.core :as re-frame]
            [react :as react]))


(defn blank->nil
  [s]
  (when (and (string? s)
             (not (str/blank? s)))
    s))


(defn dark-mode->theme
  [dark-mode?]
  (if dark-mode? "dark" "light"))


(defn featurebase-token
  [uid]
  (let [stored @(re-frame/subscribe [:app/prop :app/featurebase-jwt])]
    (when (= uid (:uid stored))
      stored)))


(defn ready?
  "True once the workspace has resolved for the current user's JWT and
  Changelog has initialized. Embeds gate on this instead of re-booting."
  []
  (boolean @(re-frame/subscribe [:app/prop :app/featurebase-ready?])))


(defn changelog-unviewed?
  []
  (boolean @(re-frame/subscribe [:app/prop :app/featurebase-changelog-unviewed?])))


(def ^:private not-ready
  {:app/featurebase-ready?              false
   :app/featurebase-changelog-unviewed? false})


(defn bootable?
  [{:keys [app-id jwt]}]
  (boolean
   (and (blank->nil app-id)
        (blank->nil jwt))))


(def ^:private max-synced-flags 20)


(defn ->synced-flags
  [variant]
  (let [mapping (js->clj (try-parse-features variant))]
    (when (and (map? mapping)
               (<= (count mapping) max-synced-flags)
               (every? (fn [[flag attribute]]
                         (and (blank->nil flag)
                              (string? attribute)
                              (re-matches #"[a-z][a-z0-9_]{0,63}" attribute)))
                       mapping)
               (= (count mapping) (count (set (vals mapping)))))
      mapping)))


(defn ->custom-fields
  [synced-flags loaded-user-id user-id enabled?]
  (when (and loaded-user-id (= loaded-user-id user-id))
    (into {}
          (map (fn [[flag attribute]]
                 [attribute (str (boolean (enabled? flag)))]))
          synced-flags)))


(defn ->settings
  [{:keys [app-id jwt dark-mode? custom-fields]}]
  (cond-> {:appId (blank->nil app-id)
           :messenger false
           :theme (dark-mode->theme dark-mode?)}
    (blank->nil jwt) (assoc :featurebaseJwt jwt)
    (seq custom-fields) (assoc :customFields custom-fields)))


(defn ->changelog-config
  [{:keys [dark-mode?]}]
  {:theme (dark-mode->theme dark-mode?)
   :popup {:enabled true}})


(defn ->boot-key
  [{:keys [app-id jwt user dark-mode?]}]
  (str/join "|" [(or (blank->nil app-id) "")
                 (or (:user/uid user) "")
                 (or (:user/user user) "")
                 (if-let [jwt (blank->nil jwt)] (hash jwt) "")
                 (dark-mode->theme dark-mode?)]))


(defn needs-reinit?
  [applied-key next-key]
  (not= applied-key next-key))


(defn jwt-lifetime-ms
  "`exp - iat` from the unverified payload, for scheduling only. Lifetime rather
  than `exp` keeps the schedule independent of the browser clock."
  [jwt]
  (try
    (let [payload (-> (second (str/split jwt #"\."))
                      (str/replace #"-" "+")
                      (str/replace #"_" "/")
                      js/atob
                      js/JSON.parse)
          exp     (j/get payload :exp)
          iat     (j/get payload :iat)]
      (when (and (number? exp) (number? iat) (< iat exp))
        (* 1000 (- exp iat))))
    (catch :default _ nil)))


(defn classify-token-response
  [error resp requested-uid current-uid now]
  (cond
    (or (nil? requested-uid) (not= requested-uid current-uid))
    {:outcome :stale}

    (some? error)
    {:outcome (if (#{401 403} (:status error)) :rejected :failed)}

    :else
    (let [jwt      (blank->nil (:featurebaseJwt resp))
          lifetime (some-> jwt jwt-lifetime-ms)]
      (if lifetime
        {:outcome :renewed
         :token   {:jwt jwt :uid requested-uid :expires-at (+ now lifetime)}}
        {:outcome :unreadable}))))


(def ^:private refresh-margin-ms (* 2 60 1000))
(def ^:private refresh-retry-ms (* 60 1000))

(defn refresh-delay
  [expires-at now]
  (max refresh-retry-ms (- expires-at refresh-margin-ms now)))


(defn- fetch-token-request [_ [uid]]
  {:http-xhrio
   {:id        :featurebase-fetch-token-request
    :method    :GET
    :url       (str AUTH_API_HOST "/integrations/featurebase/token")
    :cb-params uid}})

(defn- fetch-token-callback [_ [error resp uid]]
  (when (some? error)
    (js/console.warn "Featurebase: failed to fetch Gatekeeper token")
    (js/console.error error))
  (let [{:keys [outcome token]} (classify-token-response
                                 error
                                 resp
                                 uid
                                 (:user/uid @(re-frame/subscribe [:user/pull]))
                                 (js/Date.now))]
    (case outcome
      :renewed
      {:dispatch [:app/update {:app/featurebase-jwt token}]}

      ;; Permanent for this session: keep the current token until it expires but
      ;; stop renewing. On initial load there is no token, so only the stopped
      ;; flag is stored and Featurebase stays unavailable.
      (:rejected :unreadable)
      (do (when (= outcome :unreadable)
            (js/console.warn "Featurebase: Gatekeeper returned an unreadable token"))
          {:dispatch [:app/update {:app/featurebase-jwt (assoc (featurebase-token uid)
                                                               :uid uid
                                                               :renewal-stopped? true)}]})

      nil)))

(reg-event-http
 :featurebase.api/fetch-token
 fetch-token-request
 fetch-token-callback
 :authenticate? false)


(defonce ^:private applied-boot-key (atom nil))
(defonce ^:private latest-custom-fields (atom nil))
(defonce ^:private synced-custom-fields (atom nil))


(defn- safely!
  "Returns true when `f` ran without throwing."
  [label f]
  (try
    (f)
    true
    (catch :default error
      (js/console.warn (str "Featurebase: " label " failed"))
      (js/console.error error)
      false)))


(defn- apply-config!
  [ctx boot-key]
  (let [current? #(= boot-key @applied-boot-key)]
    (if (safely! "Featurebase" #(Featurebase (clj->js (->settings ctx))))
      (.then (resolveOrganization (:app-id ctx))
             (fn [_]
               (when (current?)
                 (let [[uid fields] @latest-custom-fields
                       settings     (-> (cond-> ctx
                                          (= uid (:user/uid (:user ctx))) (assoc :custom-fields fields))
                                        ->settings
                                        (assoc :hideDefaultLauncher true))]
                   (when (and (safely! "boot" #(boot (clj->js settings)))
                              (:customFields settings))
                     (reset! synced-custom-fields [uid fields])))
                 (safely! "setTheme" #(setTheme (dark-mode->theme (:dark-mode? ctx))))
                 (dispatch [:app/update
                            (if (safely! "initChangelog"
                                         #(initChangelog (clj->js (->changelog-config ctx))))
                              {:app/featurebase-ready? true}
                              not-ready)])))
             (fn [error]
               (js/console.warn "Featurebase: resolveOrganization failed")
               (js/console.error error)
               (when (current?)
                 (dispatch [:app/update not-ready]))))
      (dispatch [:app/update not-ready]))))


(defn- teardown!
  []
  (reset! applied-boot-key nil)
  (reset! synced-custom-fields nil)
  (dispatch [:app/update not-ready])
  (safely! "shutdown" shutdown)
  (safely! "destroyChangelog" destroyChangelog)
  (safely! "clearConfig" clearConfig))


(defn open-changelog!
  []
  (when (ready?)
    (safely! "openChangelogPopup" #(openChangelogPopup))))


;; featurebase-js@1.0.3 has no public change event for the Changelog unread
;; count: the runtime reports it via an `init_changelog_widget` callback that
;; `initChangelog` never forwards, and fills it asynchronously once the widget
;; iframe loads. So we poll the synchronous, in-memory getter while ready.
(def ^:private unviewed-poll-ms 5000)

(defn- poll-unviewed!
  [boot-key]
  (let [last-value (volatile! nil)
        interval   (volatile! nil)
        sync!      (fn []
                     (when (= boot-key @applied-boot-key)
                       (try
                         (let [n         (getUnviewedChangelogCount)
                               unviewed? (and (number? n) (pos? n))]
                           (when (not= unviewed? @last-value)
                             (vreset! last-value unviewed?)
                             (dispatch [:app/update {:app/featurebase-changelog-unviewed? unviewed?}])))
                         (catch :default error
                           (js/clearInterval @interval)
                           (js/console.warn "Featurebase: getUnviewedChangelogCount failed")
                           (js/console.error error)))))]
    (vreset! interval (js/setInterval sync! unviewed-poll-ms))
    #(js/clearInterval @interval)))


;; Timers can fall behind wall-clock JWT expiry across system sleep, so an
;; overdue refresh also runs when the page becomes visible again.
(defn- schedule-refresh!
  [expires-at refresh!]
  (let [timeout (volatile! nil)
        due-at  (volatile! nil)]
    (letfn [(arm! []
              (let [now (js/Date.now)
                    ms  (refresh-delay expires-at now)]
                (vreset! due-at (+ now ms))
                (vreset! timeout (js/setTimeout fire! ms))))
            (fire! []
              (refresh!)
              (arm!))
            (on-visibility-change []
              (when (and (= "visible" (j/get js/document :visibilityState))
                         (>= (js/Date.now) @due-at))
                (js/clearTimeout @timeout)
                (fire!)))]
      (arm!)
      (js/document.addEventListener "visibilitychange" on-visibility-change)
      #(do (js/clearTimeout @timeout)
           (js/document.removeEventListener "visibilitychange" on-visibility-change)))))


(defn logout!
  []
  (teardown!)
  (safely! "runtime shutdown" #(j/call js/window :Featurebase "shutdown"))
  (dispatch [:app/update {:app/featurebase-jwt nil}]))

(reg-fx :featurebase/logout (fn [_] (logout!)))


(defn use-lifecycle
  []
  (let [user       @(re-frame/subscribe [:user/pull])
        dark-mode? @(re-frame/subscribe [:app/prop :app/dark-mode?])
        ready      (ready?)
        app-id     (blank->nil FEATUREBASE_APP_ID)
        uid        (blank->nil (:user/uid user))
        token      (featurebase-token uid)
        jwt        (:jwt token)
        expires-at (:expires-at token)
        stopped?   (boolean (:renewal-stopped? token))
        variant    @(re-frame/subscribe [:unleash/get-variant "fe.featurebase-attributes"])
        payload    (get-in variant [:payload :value])
        synced     (->synced-flags variant)
        fields     (->custom-fields synced
                                    @(re-frame/subscribe [:unleash/loaded-user-id])
                                    (:user/user user)
                                    #(deref (re-frame/subscribe [:unleash/is-enabled %])))
        ctx        {:app-id     FEATUREBASE_APP_ID
                    :jwt        jwt
                    :user       user
                    :dark-mode? (boolean dark-mode?)}
        boot-key   (->boot-key ctx)]

    (react/useEffect
     (fn []
       (if (and uid app-id (not stopped?))
         (let [fetch! #(dispatch [:featurebase.api/fetch-token uid])]
           (when-not jwt
             (fetch!))
           ;; Without a token, "expiring now" gives the 60-second retry cadence.
           (schedule-refresh! (or expires-at (js/Date.now)) fetch!))
         js/undefined))
     #js [uid app-id jwt expires-at stopped?])

    (react/useEffect
     (fn []
       (try
         (let [bootable (bootable? ctx)]
           (cond
             (and bootable (needs-reinit? @applied-boot-key boot-key))
             (do (reset! applied-boot-key boot-key)
                 (apply-config! ctx boot-key))

             (not bootable)
             (do (reset! applied-boot-key nil)
                 (dispatch [:app/update not-ready]))))
         (catch :default error
           (js/console.warn "Featurebase: failed to apply configuration")
           (js/console.error error)))
       js/undefined)
     #js [boot-key])

    (react/useEffect
     (fn []
       (if ready
         (poll-unviewed! boot-key)
         js/undefined))
     #js [ready boot-key])

    (react/useEffect
     (fn []
       (when (and ^boolean goog.DEBUG (some? payload) (nil? synced))
         (js/console.warn "Featurebase: ignoring invalid fe.featurebase-attributes payload"))
       js/undefined)
     #js [payload])

    (react/useEffect
     (fn []
       (let [latest [uid fields]]
         (reset! latest-custom-fields latest)
         (when (and ready jwt (seq fields) (not= latest @synced-custom-fields))
           (when (safely! "update" #(update-user (clj->js {:featurebaseJwt jwt :customFields fields})))
             (reset! synced-custom-fields latest))))
       js/undefined)
     #js [ready uid jwt (hash fields)])

    (react/useEffect
     (fn []
       teardown!)
     #js [])

    nil))
